import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/cn';

// Plan 08-03 — multi-select chips for event-type groups. Stateless: parent
// owns selection (typically driven from URL via `useListQueryRoute`).
// Empty selection = «all».

export interface EventTypeSelectProps<TGroup extends string> {
  groups: readonly TGroup[];
  selected: readonly TGroup[];
  onChange: (next: TGroup[]) => void;
  className?: string;
}

export const EventTypeSelect = <TGroup extends string>({
  groups,
  selected,
  onChange,
  className,
}: EventTypeSelectProps<TGroup>) => {
  const selectedSet = new Set<string>(selected);
  const toggle = (g: TGroup): void => {
    const next = selectedSet.has(g) ? selected.filter((s) => s !== g) : [...selected, g];
    onChange(next);
  };

  return (
    <div
      className={cn('flex flex-wrap items-center gap-2', className)}
      role="group"
      aria-label="Event types"
    >
      {groups.map((g) => {
        const active = selectedSet.has(g);
        return (
          <button
            type="button"
            key={g}
            onClick={() => {
              toggle(g);
            }}
            aria-pressed={active}
            className={cn('focus:outline-none', !active && 'opacity-60 hover:opacity-100')}
          >
            <Badge variant={active ? 'default' : 'outline'}>{g}</Badge>
          </button>
        );
      })}
    </div>
  );
};
